/**
 * MultiMarketStack — #409 Phases 3-5c + #456 collapsible card refactor.
 *
 * Vertical stack of per-market sections on the kamp-side. Each section
 * is a `<details>` element (semantic disclosure):
 *
 *   - Collapsed (default): label + chevron + OddsMarket card visible.
 *     One vertical scroll shows the full market list with prices —
 *     punter can scan + tap-to-bet without expanding anything.
 *   - Expanded: per-market AI audio chrome (play button, no autoplay)
 *     followed by the LayoutRenderer body (stat widgets).
 *
 * The AI analysis used to live in a standalone `ProseStoriesRail` chip
 * row above this stack. That made the audio feel separate from the
 * stats it argues. #456 moves the audio INSIDE each market section so
 * "the 1X2 analysis lives in the 1X2 widget" (Johan's words).
 *
 * Phase 4: top-3 markets are auto-warmed on mount (hook fires /warm,
 * polls /market-layouts until they land).
 *
 * Phase 5: tail markets (#4-5) use IntersectionObserver. When a tail
 * section scrolls into view, render calls hook.triggerTailWarm(marketId)
 * which fires /warm + auto-refreshes a couple of seconds later. Idempotent
 * server-side (Redis SETNX dedup), so the observer firing twice on
 * scroll-into-view-then-out-then-back-in costs nothing extra.
 *
 * Phase 5c: odds are picked SERVER-SIDE by the engine based on
 * cms.markets_catalog.odds_template + odds_line_selector. The
 * /market-layouts response includes `oddsByMarketId` — a map of
 * marketId → VideoOddsStripMarket. Render is dumb: looks up the
 * section's marketId, runs stripMarketToDisplay to flatten to
 * DisplayMarket, and renders via OddsMarket.
 *
 * #456: lazy-mount expanded body via `hasBeenOpened` so first paint
 * stays cheap (no LayoutRenderer trees mounted for collapsed cards).
 * Once a section opens, the tree stays mounted across subsequent
 * toggles so scroll position + audio playback survive collapse.
 *
 * Locked behaviours (per Johan):
 *   - All collapsed by default. NO defaultOpen on any section.
 *   - NO autoplay. Audio fires only on explicit play-button tap.
 *
 * Failure modes (all silent — IHS bundle below remains the load-bearing
 * content):
 *   - Layout fetch error → null render
 *   - Empty everything → null render
 *   - LayoutRenderer Zod failure inside ONE section → ErrorBoundary
 *     scoped to that section only; siblings continue
 *   - Engine returns oddsByMarketId without an entry for this marketId
 *     → summary omits the odds block (suspended, unmapped, upstream out)
 *   - Prose-Stories /available endpoint errors → audio chrome hidden
 *     for ALL sections; stats + odds still render
 *
 * Ranked deck refactor (2026-07): the data fetch now lives in
 * `MarketLayoutsProvider` (same house pattern as TenantConfigProvider)
 * so the deck path on MatchDetailScreen can deal individual market
 * sections (`<DeckMarketSection marketId=…>`) interleaved with other
 * units while sharing ONE market-layouts fetch. `MultiMarketStack`
 * keeps working standalone for the legacy fixed-order path by wrapping
 * the provider around the full section list. Both paths build sections
 * through the same `sectionEntryFor` + `renderSectionEntry` pair — one
 * code path, no duplicated section-building logic.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useMarketLayouts } from '../../hooks/useMarketLayouts.js';
import type { MarketLayoutsResponse, MatchInsight } from '../../api/createEngineClient.js';
import { DataGemCard } from './DataGemCard.js';
import { useTranslation } from 'react-i18next';
import { useTenantConfig } from '../../tenant/useTenantConfig.js';
import { useEngineClient } from '../../api/EngineClientProvider.js';
import type {
  ProseStoriesAvailable,
  ProseStoryAvailableMarket,
  ProseStoryPackageDTO,
} from '../../api/createEngineClient.js';
import { reportError } from '../../observability.js';
import { LayoutRenderer } from '../layout/LayoutRenderer.js';
import { ErrorBoundary } from '../particles/ErrorBoundary.js';
import OddsMarket from '../particles/OddsMarket.js';
import { Surface } from '../primitives/Surface.js';
import {
  stripMarketToDisplay,
  type StripOutcomeLabels,
} from '../../odds/stripMarketsToDisplay.js';
import { useAdapterStrings } from '../../odds/useAdapterStrings.js';
import type { DisplayMarket, VideoOddsStripMarket } from '@aamp/shared-types';
import styles from './MultiMarketStack.module.css';

interface MultiMarketStackProps {
  eventId: string;
}

// Phase 4 contract: the hook auto-warms the top N markets on mount
// (`WARM_TOP_N` in useMarketLayouts.ts); everything else warms on
// scroll-into-view. The render side no longer needs its own index —
// warm-on-expand fires for EVERY pending market (see scrollProps below).

// ---------------------------------------------------------------------------
// MarketLayoutsProvider — ONE market-layouts fetch shared by every market
// section below it (mirrors the TenantConfigProvider house pattern). The
// legacy path wraps it inside `MultiMarketStack`; the ranked-deck path on
// MatchDetailScreen wraps the whole page middle so interleaved
// `<DeckMarketSection>` units share the same data + warm + audio state.
// ---------------------------------------------------------------------------

interface MarketLayoutsContextValue {
  eventId: string;
  data: MarketLayoutsResponse | null;
  loading: boolean;
  error: Error | null;
  triggerTailWarm: (marketId: string) => void;
  audioByMarketId: Record<string, ProseStoryAvailableMarket | undefined>;
  decimalSeparator: '.' | ',';
  /** Catalogue outcome words for strips that arrive without labels. */
  outcomeLabels: StripOutcomeLabels;
}

const MarketLayoutsContext = createContext<MarketLayoutsContextValue | null>(null);

interface MarketLayoutsProviderProps {
  eventId: string;
  children: ReactNode;
}

export function MarketLayoutsProvider({
  eventId,
  children,
}: MarketLayoutsProviderProps): ReactElement {
  const { data, loading, error, triggerTailWarm } = useMarketLayouts(eventId);

  // Tenant config drives the odds-pill decimal separator.
  const tenant = useTenantConfig();
  const decimalSeparator: '.' | ',' =
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? tenant.config.market.decimalSeparator
      : '.';

  // #456 — discover which markets have prose-Stories available. ONE call
  // per page load, shared across all sections so we don't fan out N
  // identical fetches. When the tenant has prose Stories disabled OR the
  // call errors, `audioByMarketId` stays empty and sections skip the
  // audio chrome. Stats + odds are unaffected.
  const audioByMarketId = useProseAvailableByMarket(eventId);

  // The catalogue's own outcome words, resolved once for the whole page.
  // A strip that arrives without labels must not fall back to English on a
  // Danish board (the /news "Yes/No" defect, 2026-09-08).
  const outcomeLabels = useAdapterStrings();

  const value = useMemo<MarketLayoutsContextValue>(
    () => ({
      eventId,
      data,
      loading,
      error,
      triggerTailWarm,
      audioByMarketId,
      decimalSeparator,
      outcomeLabels,
    }),
    [
      eventId,
      data,
      loading,
      error,
      triggerTailWarm,
      audioByMarketId,
      decimalSeparator,
      outcomeLabels,
    ],
  );

  return (
    <MarketLayoutsContext.Provider value={value}>
      {children}
    </MarketLayoutsContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Shared section building — ONE code path for the legacy stack AND the
// deck path. `sectionEntryFor` resolves a marketId against the fetched
// data (cached → body, pending → skeleton, unknown → null);
// `renderSectionEntry` turns an entry into the `<MarketSection>` with
// the same warm-on-expand + odds + audio props in both paths.
// ---------------------------------------------------------------------------

interface SectionEntry {
  marketId: string;
  label: string;
  body: ReactNode | null; // null => render skeleton
  oddsMarket: DisplayMarket | null;
}

/**
 * Look up the engine-shipped odds card for a catalog marketId and flatten
 * to DisplayMarket. Returns null when engine omitted the entry
 * (suspended / unpriced / upstream out / odds_template = NULL).
 */
function oddsForMarket(
  data: MarketLayoutsResponse,
  marketId: string,
  labels: StripOutcomeLabels,
): DisplayMarket | null {
  const oddsByMarketId = (data.oddsByMarketId ?? {}) as Record<
    string,
    VideoOddsStripMarket | undefined
  >;
  const strip = oddsByMarketId[marketId];
  if (!strip) return null;
  try {
    return stripMarketToDisplay(strip, { labels });
  } catch {
    // Defensive: a malformed entry should NEVER take the section down.
    // The engine validates shape via the picker, but transit / future
    // schema drift could put a bad object in the map.
    return null;
  }
}

function sectionEntryFor(
  data: MarketLayoutsResponse,
  marketId: string,
  labels: StripOutcomeLabels,
): SectionEntry | null {
  const cached = data.marketLayouts.find((e) => e.marketId === marketId);
  if (cached) {
    return {
      marketId,
      label: cached.label,
      body: (
        <ErrorBoundary>
          <LayoutRenderer
            plan={cached.plan as Parameters<typeof LayoutRenderer>[0]['plan']}
          />
        </ErrorBoundary>
      ),
      oddsMarket: oddsForMarket(data, marketId, labels),
    };
  }
  // Design audit #478 — engine ships displayName + categoryKey for
  // pending markets too, so the section header reads identically
  // whether the body is loaded or skeleton-loading.
  const pendingEntry = data.pending.find((e) => e.marketId === marketId);
  if (pendingEntry) {
    return {
      marketId,
      label: pendingEntry.label,
      body: null,
      // Odds load independently of the LayoutPlan compose, so pending
      // sections can still show the bet slip while the analyst write-up
      // is being generated. This is the "shows up FAST" moment.
      oddsMarket: oddsForMarket(data, marketId, labels),
    };
  }
  // Unknown marketId (catalog drift between deck + market-layouts) —
  // render nothing, consistent with IhsSlot's unknown-template handling.
  return null;
}

/**
 * Telemetry seams for the ranked-deck path (Phase 3 telemetry-lite).
 * Passed only by `DeckMarketSection` — the legacy stack records nothing,
 * so the beacons stay scoped to the surface the feedback loop reorders.
 */
interface SectionTelemetry {
  /** Fires on every open toggle of the section (dedupe lives in the
   *  telemetry queue, not here). */
  onExpand?: () => void;
  /** Fires when the punter taps an odds pill in the collapsed summary. */
  onOddsClick?: () => void;
}

/** The strongest significant jewel bound to a market, or null. Drives the
 *  attached gem atom that sits ON the market's section (Johan 2026-08-01:
 *  "stats about the goalscorer next to the goalscorer odds"). */
function jewelForMarket(
  ctx: MarketLayoutsContextValue,
  marketId: string,
): MatchInsight | null {
  return ctx.data?.topInsights?.find((j) => j.marketId === marketId) ?? null;
}

function renderSectionEntry(
  ctx: MarketLayoutsContextValue,
  entry: SectionEntry,
  telemetry?: SectionTelemetry,
): ReactElement {
  const jewel = jewelForMarket(ctx, entry.marketId);
  const isPending = entry.body === null;
  // Conditional-spread for optional props — under
  // `exactOptionalPropertyTypes`, passing `prop={undefined}` to a
  // declared-optional prop is a TS error. Spread the prop only
  // when its value is defined.
  const bodyProps = entry.body !== null ? { body: entry.body } : {};
  const oddsProps = entry.oddsMarket ? { oddsMarket: entry.oddsMarket } : {};
  const audioMarket = ctx.audioByMarketId[entry.marketId];
  const audioProps = audioMarket ? { audioInfo: audioMarket } : {};
  // Warm-on-expand for EVERY pending market (the full uncapped stack),
  // not just the near tail. Fires once on first open (see MarketSection).
  const scrollProps = isPending
    ? { onScrollIntoView: (): void => ctx.triggerTailWarm(entry.marketId) }
    : {};
  const expandProps = telemetry?.onExpand ? { onExpand: telemetry.onExpand } : {};
  const oddsClickProps = telemetry?.onOddsClick
    ? { onOddsClick: telemetry.onOddsClick }
    : {};
  const section = (
    <MarketSection
      key={entry.marketId}
      eventId={ctx.eventId}
      marketId={entry.marketId}
      label={entry.label}
      pending={isPending}
      decimalSeparator={ctx.decimalSeparator}
      {...bodyProps}
      {...oddsProps}
      {...audioProps}
      {...scrollProps}
      {...expandProps}
      {...oddsClickProps}
    />
  );
  if (!jewel) return section;
  // Gem atom attached ON the section — the fact directly above the bet
  // it argues. `attached` variant: no CTA/link, the odds are right here.
  return (
    <div key={entry.marketId} className={styles.sectionWithGem}>
      <ErrorBoundary>
        <DataGemCard
          eventId=""
          gem={{ ...jewel, parts: jewel.parts as Record<string, unknown> | null }}
          variant="attached"
        />
      </ErrorBoundary>
      {section}
    </div>
  );
}

/**
 * OrphanGems — significant jewels whose market has NO section on this
 * page (not composed, not pending). Rendered where the retired
 * aggregate "Most interesting" card used to sit, as individual atoms —
 * nothing mined is lost when its market isn't dealt. Context-fed: zero
 * extra requests.
 */
export function OrphanGems(): ReactElement | null {
  const ctx = useContext(MarketLayoutsContext);
  if (!ctx || ctx.error || !ctx.data) return null;
  const { data } = ctx;
  const onPage = new Set([
    ...data.marketLayouts.map((e) => e.marketId),
    ...data.pending.map((e) => e.marketId),
  ]);
  const orphans = (data.topInsights ?? []).filter((j) => !onPage.has(j.marketId));
  if (orphans.length === 0) return null;
  return (
    <div className={styles.stack}>
      {orphans.map((j, i) => (
        <ErrorBoundary key={`${j.marketId}-${i}`}>
          <DataGemCard
            eventId=""
            gem={{ ...j, parts: j.parts as Record<string, unknown> | null }}
            variant="inline"
          />
        </ErrorBoundary>
      ))}
    </div>
  );
}

export function MultiMarketStack({ eventId }: MultiMarketStackProps): ReactElement | null {
  // Standalone (legacy fixed-order) path: own provider around the full
  // section list. The deck path bypasses this component and mounts
  // `MarketLayoutsProvider` + `DeckMarketSection` units directly.
  return (
    <MarketLayoutsProvider eventId={eventId}>
      <MultiMarketStackSections />
    </MarketLayoutsProvider>
  );
}

function MultiMarketStackSections(): ReactElement | null {
  const ctx = useContext(MarketLayoutsContext);
  if (!ctx) return null;
  const { data, loading, error, eventId } = ctx;

  if (error) return null;
  if (!loading && data && data.marketLayouts.length === 0 && data.pending.length === 0) {
    return null;
  }
  if (loading || !data) {
    return (
      <div className={styles.stack} aria-busy="true">
        <MarketSection
          eventId={eventId}
          marketId="loading"
          label="Loading…"
          pending
        />
      </div>
    );
  }

  // Merged ordered list: cached entries first (in bettor_relevance order
  // from the engine), then pending markets (also in bettor_relevance
  // order) — unchanged legacy order.
  //
  // Content Pack C2 (the odds diet): when the engine ships `featured`
  // flags, only featured sections render upfront; the rest (plus the
  // still-composing pending list) sit behind ONE "all markets"
  // disclosure. An engine without flags → every section upfront,
  // exactly the pre-C2 page.
  const hasFeaturedFlags = data.marketLayouts.some((e) => e.featured !== undefined);
  const upfrontIds = hasFeaturedFlags
    ? data.marketLayouts.filter((e) => e.featured !== false).map((e) => e.marketId)
    : [
        ...data.marketLayouts.map((e) => e.marketId),
        ...data.pending.map((e) => e.marketId),
      ];
  const restIds = hasFeaturedFlags
    ? [
        ...data.marketLayouts.filter((e) => e.featured === false).map((e) => e.marketId),
        ...data.pending.map((e) => e.marketId),
      ]
    : [];

  return (
    <div className={styles.stack}>
      {upfrontIds.map((marketId) => {
        const entry = sectionEntryFor(data, marketId, ctx.outcomeLabels);
        return entry ? renderSectionEntry(ctx, entry) : null;
      })}
      {restIds.length > 0 ? (
        <AllMarketsDisclosure count={restIds.length}>
          {restIds.map((marketId) => {
            const entry = sectionEntryFor(data, marketId, ctx.outcomeLabels);
            return entry ? renderSectionEntry(ctx, entry) : null;
          })}
        </AllMarketsDisclosure>
      ) : null}
    </div>
  );
}

/**
 * C2 — the single "all markets" disclosure holding every non-featured
 * section. A `<details>` like the sections themselves so the affordance
 * is familiar; the label carries the hidden-market count so the punter
 * knows the depth exists without paying its height.
 */
function AllMarketsDisclosure({
  count,
  children,
}: {
  count: number;
  children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <details
      className={styles.allMarkets}
      open={open}
      onToggle={(e): void => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className={styles.allMarketsSummary}>
        {t('market.showAll', { defaultValue: 'All markets ({{count}})', count })}
      </summary>
      {open ? children : null}
    </details>
  );
}

// ---------------------------------------------------------------------------
// DeckMarketSection — ONE market section for the ranked-deck path. Must be
// mounted below `MarketLayoutsProvider`; renders the same MarketSection
// (same warm-on-expand + odds + audio props) as the legacy stack via the
// shared entry builder. Self-hides (null) while the shared fetch is
// loading, on fetch error, and for a marketId the engine data doesn't
// know — silently consistent with the stack's own failure modes and
// IhsSlot's unknown-template behaviour.
// ---------------------------------------------------------------------------

interface DeckMarketSectionProps {
  marketId: string;
  /** Telemetry-lite (Phase 3): fires on every section open. Only the
   *  deck branch on MatchDetailScreen passes these — the legacy stack
   *  stays beacon-free. */
  onExpand?: () => void;
  /** Telemetry-lite (Phase 3): fires on an odds-pill tap in this
   *  section's collapsed summary. */
  onOddsClick?: () => void;
}

export function DeckMarketSection({
  marketId,
  onExpand,
  onOddsClick,
}: DeckMarketSectionProps): ReactElement | null {
  const ctx = useContext(MarketLayoutsContext);
  if (!ctx || ctx.error || !ctx.data) return null;
  // C2 — non-featured markets never render inline in the deck; they live
  // in the trailing <DeckAllMarketsDisclosure>. Absent flag = featured.
  const layoutEntry = ctx.data.marketLayouts.find((e) => e.marketId === marketId);
  if (layoutEntry?.featured === false) return null;
  const entry = sectionEntryFor(ctx.data, marketId, ctx.outcomeLabels);
  if (!entry) return null;
  const telemetry: SectionTelemetry = {
    ...(onExpand ? { onExpand } : {}),
    ...(onOddsClick ? { onOddsClick } : {}),
  };
  // Same `.stack` wrapper as the legacy path so a single section keeps
  // the stack's width contract (max-width + centring); gap is moot for
  // one child.
  return (
    <div className={styles.stack}>{renderSectionEntry(ctx, entry, telemetry)}</div>
  );
}

/**
 * C2 — deck-path companion to the legacy stack's disclosure: renders
 * every NON-featured composed market (which DeckMarketSection skipped
 * inline) plus the pending list behind one "all markets" toggle. Mount
 * once, after the dealt deck, inside MarketLayoutsProvider. Self-hides
 * when the engine ships no featured flags (older engine → deck already
 * rendered everything inline) or when nothing is hidden.
 */
export function DeckAllMarketsDisclosure(): ReactElement | null {
  const ctx = useContext(MarketLayoutsContext);
  if (!ctx || ctx.error || !ctx.data) return null;
  const { data } = ctx;
  const hasFeaturedFlags = data.marketLayouts.some((e) => e.featured !== undefined);
  if (!hasFeaturedFlags) return null;
  const restIds = [
    ...data.marketLayouts.filter((e) => e.featured === false).map((e) => e.marketId),
    ...data.pending.map((e) => e.marketId),
  ];
  if (restIds.length === 0) return null;
  return (
    <div className={styles.stack}>
      <AllMarketsDisclosure count={restIds.length}>
        {restIds.map((marketId) => {
          const entry = sectionEntryFor(data, marketId, ctx.outcomeLabels);
          return entry ? renderSectionEntry(ctx, entry) : null;
        })}
      </AllMarketsDisclosure>
    </div>
  );
}

// ---------------------------------------------------------------------------
// useProseAvailableByMarket — one /available call, keyed by marketId so
// MarketSection can ask "do I have an AI take?" in O(1).
// ---------------------------------------------------------------------------

function useProseAvailableByMarket(
  eventId: string,
): Record<string, ProseStoryAvailableMarket | undefined> {
  const client = useEngineClient();
  const [byId, setById] = useState<Record<string, ProseStoryAvailableMarket | undefined>>({});

  useEffect(() => {
    let cancelled = false;
    setById({});
    client
      .fetchProseStoriesAvailable(eventId)
      .then((resp: ProseStoriesAvailable) => {
        if (cancelled) return;
        if (!resp.enabled || resp.markets.length === 0) {
          setById({});
          return;
        }
        const map: Record<string, ProseStoryAvailableMarket | undefined> = {};
        for (const m of resp.markets) map[m.marketId] = m;
        setById(map);
      })
      .catch((err) => {
        // Self-hide on error — the audio chrome simply never appears,
        // user still gets stats + odds.
        reportError(err instanceof Error ? err : new Error('available fetch failed'), {
          scope: 'MultiMarketStack.useProseAvailableByMarket',
          eventId,
        });
        if (!cancelled) setById({});
      });
    return (): void => {
      cancelled = true;
    };
  }, [client, eventId]);

  return byId;
}

// ---------------------------------------------------------------------------
// MarketSection — single collapsible card. Uses semantic <details>/
// <summary> for keyboard + screen-reader accessibility out of the box.
// Phase 5 adds onScrollIntoView callback for tail markets; the observer
// fires it once per mount when the section is ~25% in view.
// ---------------------------------------------------------------------------

interface MarketSectionProps {
  eventId: string;
  marketId: string;
  label: string;
  pending: boolean;
  body?: ReactNode;
  /** Phase 5 — fires ONCE when the section first enters the viewport. */
  onScrollIntoView?: () => void;
  /**
   * Phase 5b — optional odds card matched to this section's catalog
   * marketId. Shown in the COLLAPSED summary so punters see prices
   * without expanding. Undefined → odds not loaded OR no usable offer.
   */
  oddsMarket?: DisplayMarket;
  /** Locale-aware decimal separator for odds prices. */
  decimalSeparator?: '.' | ',';
  /**
   * #456 — per-market AI take info. When present + market.built, the
   * expanded body shows a play button that streams cached audio
   * instantly. When built=false, tapping it triggers a Sonnet + Haiku
   * + ElevenLabs cold-build (~30-60s). Undefined → no audio chrome.
   */
  audioInfo?: ProseStoryAvailableMarket;
  /** Telemetry-lite (Phase 3) — fires on every open toggle. The
   *  telemetry queue dedupes per page view, so no firedRef here. */
  onExpand?: () => void;
  /** Telemetry-lite (Phase 3) — fires when an odds pill in the summary
   *  card is tapped. Rides OddsMarket's existing `onSelect` seam. */
  onOddsClick?: () => void;
}

function MarketSection({
  eventId,
  marketId,
  label,
  pending,
  body,
  onScrollIntoView,
  oddsMarket,
  decimalSeparator = '.',
  audioInfo,
  onExpand,
  onOddsClick,
}: MarketSectionProps): ReactElement {
  const { t } = useTranslation();
  const ref = useRef<HTMLDetailsElement | null>(null);

  // Lazy-mount the expanded body. First paint stays cheap — closed
  // cards render only summary chrome. After the first open, the tree
  // stays mounted across collapse/expand so audio + scroll survive.
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  // Guard against repeated callbacks if the observer fires on scroll
  // back-and-forth. Belt-and-braces with the hook's own dedup set.
  const firedRef = useRef(false);

  // Compose-on-EXPAND (Johan, 2026-05-30): a pending market composes its AI
  // widgets the first time the user OPENS it — deliberate intent, bounded by
  // the per-tenant daily budget guard. (Replaces scroll-into-view prefetch so
  // the full market list can be uncapped without composing everything the user
  // merely scrolls past. Top-N still auto-warm via useMarketLayouts.)
  //
  // Card chrome (fill / border / radius / corner clipping) comes from the
  // Surface primitive (ADR-059/-060 — one card shell). The <details> stays a
  // bare disclosure element inside it: Surface cannot BE the <details>
  // (it renders a <section>), so it wraps it. `.section` keeps only the
  // open/closed state hooks; its bespoke shell block is gone. Token-for-token
  // this is the same recipe the section painted before (--bg-surface,
  // --border, --radius-card, --shadow-card: none), so rendering is unchanged.
  return (
    <Surface elevation="border" padding="none">
      <details
        ref={ref}
        className={styles.section}
        onToggle={(e): void => {
        if ((e.currentTarget as HTMLDetailsElement).open) {
          setHasBeenOpened(true);
          onExpand?.();
          if (onScrollIntoView && !firedRef.current) {
            firedRef.current = true;
            onScrollIntoView();
          }
        }
      }}
    >
      <summary className={styles.summary}>
        <div className={styles.summaryHeader}>
          <h2 className={styles.label}>
            {label}
            {/* Surface the line variant (e.g. " · -1.25" for AH, " · 2.5"
             *  for O/U) inline so the heading carries the bookmaker's
             *  current line. OddsMarket's own header is hidden below via
             *  hideTitle to prevent a dupe; this is where that detail
             *  re-appears. Defensive: only numeric line values render —
             *  guards against engine schema drift where line could ship
             *  as a string or null. */}
            {typeof oddsMarket?.line === 'number' && (
              <span className={styles.labelLine}>
                {' · '}
                {String(oddsMarket.line).replace('.', decimalSeparator)}
              </span>
            )}
          </h2>
          {/* Chevron rotates via CSS sibling selector on parent <details
           *  open>. aria-hidden because the disclosure state is conveyed
           *  natively by the <details> element. */}
          <svg
            className={styles.chevron}
            width="14"
            height="14"
            viewBox="0 0 16 16"
            aria-hidden="true"
          >
            <path
              d="M4 6 L8 10 L12 6"
              stroke="currentColor"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        {oddsMarket ? (
          <div className={styles.summaryOdds}>
            <ErrorBoundary>
              <OddsMarket
                market={oddsMarket}
                decimalSeparator={decimalSeparator}
                hideTitle
                {...(onOddsClick ? { onSelect: (): void => onOddsClick() } : {})}
              />
            </ErrorBoundary>
          </div>
        ) : null}
      </summary>

      {hasBeenOpened ? (
        <div className={styles.expanded}>
          {audioInfo ? (
            <MarketAudio
              eventId={eventId}
              marketId={marketId}
              marketLabel={audioInfo.marketLabel || label}
              built={audioInfo.built}
              {...(audioInfo.audioUrl !== undefined ? { prebuiltAudioUrl: audioInfo.audioUrl } : {})}
              {...(audioInfo.durationMs !== undefined ? { prebuiltDurationMs: audioInfo.durationMs } : {})}
            />
          ) : null}
          <div className={styles.body}>
            {pending ? (
              <div className={styles.skeleton} aria-busy="true" aria-label={t('multiMarket.loadingAria')}>
                <span className={`${styles.skeletonBlock} ${styles.skeletonHeader}`} />
                <span className={`${styles.skeletonBlock} ${styles.skeletonRow}`} />
                <span className={`${styles.skeletonBlock} ${styles.skeletonRow}`} />
                <span className={`${styles.skeletonBlock} ${styles.skeletonRowShort}`} />
              </div>
            ) : (
              body
            )}
          </div>
        </div>
      ) : null}
      </details>
    </Surface>
  );
}

// ---------------------------------------------------------------------------
// MarketAudio — inline per-market AI audio chrome.
//
// Three states:
//   1. Idle (built=true, never tapped) — green play button + label.
//      Tap → fetch story + render <audio controls> + start playing.
//   2. Idle (built=false, cold start) — sparkle icon + "Generate"
//      sublabel. Tap → fire requestProseStory (30-60s); spinner shows
//      until audio is ready; then auto-plays.
//   3. Loaded — full HTML5 audio control bar replaces the chrome.
//
// NO autoplay anywhere — every audio start is the result of an
// explicit user tap on the play button. Honors Johan's lock from
// 2026-05-23 ("Reveal the play-button but dont autoplay anything").
// ---------------------------------------------------------------------------

interface MarketAudioProps {
  eventId: string;
  marketId: string;
  marketLabel: string;
  built: boolean;
  prebuiltAudioUrl?: string;
  prebuiltDurationMs?: number;
}

function MarketAudio({
  eventId,
  marketId,
  marketLabel,
  built,
  prebuiltAudioUrl,
  prebuiltDurationMs,
}: MarketAudioProps): ReactElement {
  const { t } = useTranslation();
  const client = useEngineClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [audioUrl, setAudioUrl] = useState<string | undefined>(
    built ? prebuiltAudioUrl : undefined,
  );
  const [durationMs, setDurationMs] = useState<number | undefined>(
    built ? prebuiltDurationMs : undefined,
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  function handlePlay(): void {
    // Already have a usable URL (built=true with prebuilt URL) — just
    // mount the <audio> element and let the user press its native play.
    // We deliberately do NOT call .play() programmatically; the user
    // tapped to REVEAL the controls, not necessarily to start audio.
    // (Per Johan's lock: user must take a specific intentional action
    // on the control itself.)
    if (audioUrl) {
      setPhase('ready');
      return;
    }
    // Cold path — Sonnet + Haiku + ElevenLabs + S3 upload (~30-60s).
    setPhase('loading');
    setErrorMsg(null);
    client
      .requestProseStory(eventId, marketId, marketLabel)
      .then((resp: ProseStoryPackageDTO) => {
        setAudioUrl(resp.audioUrl);
        setDurationMs(resp.durationMs);
        setPhase('ready');
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : t('multiMarket.generateAudioError');
        setErrorMsg(msg);
        setPhase('error');
        reportError(err instanceof Error ? err : new Error('story request failed'), {
          scope: 'MultiMarketStack.MarketAudio',
          eventId,
          marketId,
        });
      });
  }

  // When the audio source becomes available AFTER a cold build, kick
  // playback automatically — the user has been staring at the spinner
  // for 30+ seconds and the play tap is their intent to listen.
  useEffect(() => {
    if (phase === 'ready' && audioRef.current && !built) {
      // built=false means we came in via cold-build, so auto-start.
      // For built=true paths we mount controls but don't auto-start
      // (the user's tap was to REVEAL, not necessarily play).
      audioRef.current.play().catch(() => {
        /* autoplay may be blocked by browser policy — controls still work */
      });
    }
  }, [phase, built]);

  // Phase 1+2 idle chrome — green play button.
  if (phase === 'idle' || phase === 'loading' || phase === 'error') {
    return (
      <div className={styles.audioChrome}>
        <button
          type="button"
          className={`${styles.audioPlay} ${!built ? styles.audioPlayCold : ''}`}
          onClick={handlePlay}
          disabled={phase === 'loading'}
          aria-label={built ? t('multiMarket.playAria') : t('multiMarket.generateAria')}
        >
          {phase === 'loading' ? (
            <span className={styles.audioSpinner} aria-hidden="true" />
          ) : built ? (
            <svg width="12" height="14" viewBox="0 0 10 12" aria-hidden="true">
              <path d="M0 0 L10 6 L0 12 Z" fill="currentColor" />
            </svg>
          ) : (
            <span className={styles.audioSparkle} aria-hidden="true">✨</span>
          )}
        </button>
        <div className={styles.audioText}>
          <div className={styles.audioLabel}>{t('multiMarket.audioLabel')}</div>
          <div className={styles.audioSub}>
            {phase === 'loading'
              ? t('multiMarket.generating')
              : phase === 'error'
                ? (errorMsg ?? t('multiMarket.fetchAudioError'))
                : built
                  ? formatDuration(durationMs, t)
                  : t('multiMarket.tapToGenerate')}
          </div>
        </div>
      </div>
    );
  }

  // Phase 3 — audio source available. Mount native controls. The user
  // can scrub, mute, change rate. For cold-build paths we auto-start
  // (see useEffect above); for warm-cache paths we let them tap play.
  return (
    <div className={styles.audioReady}>
      {/* Radio-spot preview: the full spoken copy is rendered as text in
          the same market section — a captions track would duplicate it. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        className={styles.audioEl}
        controls
        preload="auto"
        src={audioUrl}
      />
      <div className={styles.audioReadySub}>{t('multiMarket.audioLabel')} · {marketLabel}</div>
    </div>
  );
}

function formatDuration(ms: number | undefined, t: (key: string) => string): string {
  if (!ms || ms <= 0) return t('multiMarket.tapToHear');
  const sec = Math.round(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')} ${t('multiMarket.durationSuffix')}`;
}

export default MultiMarketStack;
